import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  Stack,
  Typography,
  useTheme,
  IconButton,
  Divider,
} from "@mui/material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { LoadingButton } from "@mui/lab";
import BaseModal from "./global/BaseModal";
import { CardPreferences } from "../services/cardPreferences.service";

// Card IDs mapping
export const CARD_IDS = {
  scopeOfWork: "scopeOfWork",
  confidentialInfo: "confidentialInfo",
  workDocuments: "workDocuments",
  startTime: "startTime",
  estimatedHours: "estimatedHours",
  tasks: "tasks",
  requirements: "requirements",
  additionalSettings: "additionalSettings",
  workSite: "workSite",
  pricing: "pricing",
  manager: "manager",
  internalNotes: "internalNotes",
  metadata: "metadata",
} as const;

export const CARD_LABELS: Record<string, string> = {
  [CARD_IDS.scopeOfWork]: "Scope of Work",
  [CARD_IDS.confidentialInfo]: "Confidential Information",
  [CARD_IDS.workDocuments]: "Work Documents",
  [CARD_IDS.startTime]: "Start Time",
  [CARD_IDS.estimatedHours]: "Estimated Amount of Hours",
  [CARD_IDS.tasks]: "Tasks",
  [CARD_IDS.requirements]: "Requirements",
  [CARD_IDS.additionalSettings]: "Additional Settings",
  [CARD_IDS.workSite]: "Work Site",
  [CARD_IDS.pricing]: "Pricing",
  [CARD_IDS.manager]: "Manager",
  [CARD_IDS.internalNotes]: "Internal Notes",
  [CARD_IDS.metadata]: "Metadata",
};

// Default order (all cards)
export const DEFAULT_CARD_ORDER = Object.values(CARD_IDS);

interface SortableItemProps {
  id: string;
  label: string;
  isHidden: boolean;
  onToggleVisibility: (id: string) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({
  id,
  label,
  isHidden,
  onToggleVisibility,
}) => {
  const theme = useTheme();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 1.5,
        px: 2,
        mb: 1,
        borderRadius: 2,
        backgroundColor: isDragging
          ? theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.1)"
            : "rgba(0, 0, 0, 0.05)"
          : theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(0, 0, 0, 0.02)",
        border: `1px solid ${theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(0, 0, 0, 0.08)"
          }`,
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.08)"
              : "rgba(0, 0, 0, 0.04)",
          borderColor: theme.palette.primary.main + "40",
        },
      }}
    >
      <IconButton
        {...attributes}
        {...listeners}
        sx={{
          cursor: "grab",
          color: "text.secondary",
          "&:active": {
            cursor: "grabbing",
          },
          "&:hover": {
            color: "primary.main",
            backgroundColor: "primary.light" + "20",
          },
        }}
      >
        <DragIndicatorIcon />
      </IconButton>
      <Typography
        sx={{
          flex: 1,
          fontWeight: 500,
          opacity: isHidden ? 0.5 : 1,
          textDecoration: isHidden ? "line-through" : "none",
        }}
      >
        {label}
      </Typography>
      <IconButton
        onClick={() => onToggleVisibility(id)}
        sx={{
          color: isHidden ? "text.secondary" : "primary.main",
          "&:hover": {
            backgroundColor: "primary.light" + "20",
          },
        }}
      >
        {isHidden ? <VisibilityOffIcon /> : <VisibilityIcon />}
      </IconButton>
    </ListItem>
  );
};

interface CustomizeLayoutModalProps {
  open: boolean;
  onClose: () => void;
  preferences: CardPreferences | undefined;
  onSave: (preferences: { cardOrder: string[]; hiddenCards: string[]; }) => Promise<void>;
}

const CustomizeLayoutModal: React.FC<CustomizeLayoutModalProps> = ({
  open,
  onClose,
  preferences,
  onSave,
}) => {
  const theme = useTheme();
  const [cardOrder, setCardOrder] = React.useState<string[]>([]);
  const [hiddenCards, setHiddenCards] = React.useState<string[]>([]);
  const [isSaving, setIsSaving] = React.useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Initialize state from preferences
  React.useEffect(() => {
    if (preferences) {
      // If cardOrder is empty array or undefined, use default
      const order = (!preferences.cardOrder || preferences.cardOrder.length === 0)
        ? DEFAULT_CARD_ORDER
        : preferences.cardOrder;
      setCardOrder(order);
      setHiddenCards(preferences.hiddenCards || []);
    } else {
      setCardOrder(DEFAULT_CARD_ORDER);
      setHiddenCards([]);
    }
  }, [preferences, open]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCardOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleToggleVisibility = (cardId: string) => {
    setHiddenCards((prev) =>
      prev.includes(cardId)
        ? prev.filter((id) => id !== cardId)
        : [...prev, cardId]
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({
        cardOrder,
        hiddenCards,
      });
      onClose();
    } catch (error) {
      console.error("Failed to save preferences:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setCardOrder(DEFAULT_CARD_ORDER);
    setHiddenCards([]);
  };

  const footer = (
    <>
      <Button onClick={handleReset} disabled={isSaving}>
        Reset to Default
      </Button>
      <Button onClick={onClose} disabled={isSaving}>
        Cancel
      </Button>
      <LoadingButton
        onClick={handleSave}
        variant="contained"
        loading={isSaving}
      >
        Save Changes
      </LoadingButton>
    </>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="Customize Layout"
      footer={footer}
      maxWidth="md"
      useGlassCard={false}
    >
      <Box sx={{ p: 3 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          Drag and drop cards to reorder them. Click the eye icon to show or
          hide cards.
        </Typography>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={cardOrder}
            strategy={verticalListSortingStrategy}
          >
            <List sx={{ p: 0 }}>
              {cardOrder.map((cardId, index) => (
                <React.Fragment key={cardId}>
                  <SortableItem
                    id={cardId}
                    label={CARD_LABELS[cardId] || cardId}
                    isHidden={hiddenCards.includes(cardId)}
                    onToggleVisibility={handleToggleVisibility}
                  />
                  {index < cardOrder.length - 1 && <Divider sx={{ mx: 2 }} />}
                </React.Fragment>
              ))}
            </List>
          </SortableContext>
        </DndContext>
      </Box>
    </BaseModal>
  );
};

export default CustomizeLayoutModal;

